import { useEffect, useRef, useState } from 'react'
import type { ScoreboardEvent } from '@sportninja/common/utils/games/scoreboardSync'
import { resolveAssetUrl } from '@sportninja/common/utils/assetUrl'

const GOAL_CARD_DURATION = 4500
const PERIOD_CARD_DURATION = 3700

const ANIMATABLE_TYPES = [
  'goal',
  'period_start',
  'period_end',
  'shootout_start',
  'shootout_end',
  'game_end',
  'goalie_change',
  'offense',
]

// --- Card data interfaces ---

export interface GoalAssistData {
  name: string
  number: string
}

export interface GoalCardData {
  id: string
  playerName: string
  playerNumber: string
  playerImageUrl: string | null
  scorerTeamLogoUrl: string
  scorerTeamAbbrev: string
  scorerTeamJerseyColor: string
  goalClockTime: string
  goalValue: number
  assists: GoalAssistData[]
}

export interface PeriodCardData {
  id: string
  periodName: string
  eventType: string
  homeTeamName: string
  homeTeamLogoUrl: string
  homeJerseyColor: string
  homeScore: number
  visitorTeamName: string
  visitorTeamLogoUrl: string
  visitorJerseyColor: string
  visitorScore: number
}

export interface SubstitutionCardData {
  id: string
  goalieInName: string
  goalieInNumber: string
  hasGoalieIn: boolean
  goalieOutName: string
  goalieOutNumber: string
  teamLogoUrl: string
  teamAbbrev: string
  teamJerseyColor: string
  clockTime: string
}

export interface FoulCardData {
  id: string
  playerName: string
  playerNumber: string
  playerImageUrl: string | null
  teamLogoUrl: string
  teamAbbrev: string
  teamJerseyColor: string
  clockTime: string
  offenseType: string
  offenseSeverity: string
}

// --- Hook options ---

interface UseScoreboardAnimationsOptions {
  scoreboardEvents: ScoreboardEvent[] | null | undefined
  videoRef: { current: HTMLVideoElement | null }
  streamStartedAt: string | null | undefined
  isLive: boolean
  game: {
    homeTeamId?: string
    visitingTeamId?: string
    homeTeam?: {
      name?: string
      fullName?: string
      abbrev?: string
      imageId?: string
    }
    visitingTeam?: {
      name?: string
      fullName?: string
      abbrev?: string
      imageId?: string
    }
  } | null
  homeJerseyColor: string
  visitorJerseyColor: string
}

// --- Map scoreboard event to goal card data ---

function mapGoalCard(
  event: ScoreboardEvent,
  game: UseScoreboardAnimationsOptions['game'],
  homeJerseyColor: string,
  visitorJerseyColor: string
): GoalCardData {
  const isHomeTeam = event.team_id === game?.homeTeamId
  const scorerTeam = isHomeTeam ? game?.homeTeam : game?.visitingTeam
  const jerseyColor = isHomeTeam ? homeJerseyColor : visitorJerseyColor

  let clockTime = event.period_clock_time || ''
  if (clockTime.startsWith('00:')) {
    clockTime = clockTime.slice(3)
  }

  return {
    id: `goal-${event.occurred_at}`,
    playerName:
      event.player?.full_name ||
      scorerTeam?.fullName ||
      scorerTeam?.name ||
      scorerTeam?.abbrev ||
      '',
    playerNumber: event.player?.full_name
      ? event.player?.player_number?.toString() || ''
      : '',
    playerImageUrl: resolveAssetUrl(event.player?.image) ?? null,
    scorerTeamLogoUrl: scorerTeam?.imageId || '',
    scorerTeamAbbrev: scorerTeam?.abbrev || '',
    scorerTeamJerseyColor: jerseyColor,
    goalClockTime: clockTime,
    goalValue: event.value || 1,
    assists: (event.assists || [])
      .filter((a) => a.player)
      .map((a) => ({
        name:
          a.player!.full_name ||
          `${a.player!.name_first} ${a.player!.name_last}`,
        number: a.player!.player_number?.toString() || '',
      })),
  }
}

// --- Map scoreboard event to period card data ---

function mapPeriodCard(
  event: ScoreboardEvent,
  game: UseScoreboardAnimationsOptions['game'],
  homeJerseyColor: string,
  visitorJerseyColor: string
): PeriodCardData {
  const eventTypeMap: Record<string, string> = {
    period_start: 'period.started',
    period_end: 'period.ended',
    shootout_start: 'shootout.started',
    shootout_end: 'shootout.ended',
    game_end: 'game.finalized',
  }

  return {
    id: `${event.event_type}-${event.occurred_at}`,
    periodName:
      event.period?.type?.name || event.period?.type?.name_full || 'Period',
    eventType: eventTypeMap[event.event_type || ''] || event.event_type || '',
    homeTeamName:
      game?.homeTeam?.fullName ||
      game?.homeTeam?.name ||
      game?.homeTeam?.abbrev ||
      'Home',
    homeTeamLogoUrl: game?.homeTeam?.imageId || '',
    homeJerseyColor,
    visitorTeamName:
      game?.visitingTeam?.fullName ||
      game?.visitingTeam?.name ||
      game?.visitingTeam?.abbrev ||
      'Visitor',
    visitorTeamLogoUrl: game?.visitingTeam?.imageId || '',
    visitorJerseyColor,
    homeScore: event.home_team_score ?? 0,
    visitorScore: event.visiting_team_score ?? 0,
  }
}

// --- Map scoreboard event to substitution card data ---

function mapSubstitutionCard(
  event: ScoreboardEvent,
  game: UseScoreboardAnimationsOptions['game'],
  homeJerseyColor: string,
  visitorJerseyColor: string
): SubstitutionCardData {
  const isHomeTeam = event.team_id === game?.homeTeamId
  const team = isHomeTeam ? game?.homeTeam : game?.visitingTeam
  const jerseyColor = isHomeTeam ? homeJerseyColor : visitorJerseyColor

  let clockTime = event.period_clock_time || ''
  if (clockTime.startsWith('00:')) {
    clockTime = clockTime.slice(3)
  }

  return {
    id: `sub-${event.occurred_at}`,
    goalieInName:
      event.goalie?.full_name ||
      team?.fullName ||
      team?.name ||
      team?.abbrev ||
      '',
    goalieInNumber: event.goalie?.player_number?.toString() || '',
    hasGoalieIn: !!event.goalie,
    goalieOutName: event.previous_goalie?.full_name || '',
    goalieOutNumber: event.previous_goalie?.player_number?.toString() || '',
    teamLogoUrl: team?.imageId || '',
    teamAbbrev: team?.abbrev || '',
    teamJerseyColor: jerseyColor,
    clockTime,
  }
}

// --- Map scoreboard event to foul card data ---

function mapFoulCard(
  event: ScoreboardEvent,
  game: UseScoreboardAnimationsOptions['game'],
  homeJerseyColor: string,
  visitorJerseyColor: string
): FoulCardData {
  const isHomeTeam = event.team_id === game?.homeTeamId
  const team = isHomeTeam ? game?.homeTeam : game?.visitingTeam
  const jerseyColor = isHomeTeam ? homeJerseyColor : visitorJerseyColor

  let clockTime = event.period_clock_time || ''
  if (clockTime.startsWith('00:')) {
    clockTime = clockTime.slice(3)
  }

  return {
    id: `foul-${event.occurred_at}`,
    playerName:
      event.player?.full_name ||
      team?.fullName ||
      team?.name ||
      team?.abbrev ||
      '',
    playerNumber: event.player?.full_name
      ? event.player?.player_number?.toString() || ''
      : '',
    playerImageUrl: resolveAssetUrl(event.player?.image) ?? null,
    teamLogoUrl: team?.imageId || '',
    teamAbbrev: team?.abbrev || '',
    teamJerseyColor: jerseyColor,
    clockTime,
    offenseType: event.offense_type?.name || '',
    offenseSeverity: event.offense_severity?.name || '',
  }
}

// --- Compute absolute event time from video position ---

function getEventTimeMsFromVideo(
  video: HTMLVideoElement,
  streamStartedAt: string | null | undefined,
  isLive: boolean
): number {
  if (isLive) {
    return Date.now()
  }
  if (!streamStartedAt) {
    return 0
  }
  return Math.round(
    new Date(streamStartedAt).getTime() + video.currentTime * 1000
  )
}

// --- Hook ---

export default function useScoreboardAnimations({
  scoreboardEvents,
  videoRef,
  streamStartedAt,
  isLive,
  game,
  homeJerseyColor,
  visitorJerseyColor,
}: UseScoreboardAnimationsOptions) {
  'use memo'
  const prevTimeMs = useRef<number>(-1)
  const cooldownUntil = useRef<number>(0)
  const lastKnownEventKey = useRef<string>('')
  // Whether the initial scoreboard load already had animatable events
  const hadEventsOnMount = useRef<boolean | null>(null)

  const [activeGoalCard, setActiveGoalCard] = useState<GoalCardData | null>(
    null
  )
  const [activePeriodCard, setActivePeriodCard] =
    useState<PeriodCardData | null>(null)
  const [activeSubstitutionCard, setActiveSubstitutionCard] =
    useState<SubstitutionCardData | null>(null)
  const [activeFoulCard, setActiveFoulCard] = useState<FoulCardData | null>(
    null
  )

  const goalDismissTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const periodDismissTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const subDismissTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const foulDismissTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Store latest values in refs to avoid stale closures in event listener
  const latestRef = useRef({
    scoreboardEvents,
    isLive,
    streamStartedAt,
    game,
    homeJerseyColor,
    visitorJerseyColor,
  })
  latestRef.current = {
    scoreboardEvents,
    isLive,
    streamStartedAt,
    game,
    homeJerseyColor,
    visitorJerseyColor,
  }

  const dismissAll = () => {
    if (goalDismissTimer.current) {
      clearTimeout(goalDismissTimer.current)
      goalDismissTimer.current = null
    }
    if (periodDismissTimer.current) {
      clearTimeout(periodDismissTimer.current)
      periodDismissTimer.current = null
    }
    if (subDismissTimer.current) {
      clearTimeout(subDismissTimer.current)
      subDismissTimer.current = null
    }
    if (foulDismissTimer.current) {
      clearTimeout(foulDismissTimer.current)
      foulDismissTimer.current = null
    }
    setActiveGoalCard(null)
    setActivePeriodCard(null)
    setActiveSubstitutionCard(null)
    setActiveFoulCard(null)
  }

  const showAnimation = (event: ScoreboardEvent) => {
    const {
      game: g,
      homeJerseyColor: hjc,
      visitorJerseyColor: vjc,
    } = latestRef.current
    dismissAll()

    if (event.event_type === 'goal') {
      const card = mapGoalCard(event, g, hjc, vjc)
      setActiveGoalCard(card)
      goalDismissTimer.current = setTimeout(() => {
        setActiveGoalCard(null)
        goalDismissTimer.current = null
      }, GOAL_CARD_DURATION)
    } else if (event.event_type === 'goalie_change') {
      const card = mapSubstitutionCard(event, g, hjc, vjc)
      setActiveSubstitutionCard(card)
      subDismissTimer.current = setTimeout(() => {
        setActiveSubstitutionCard(null)
        subDismissTimer.current = null
      }, GOAL_CARD_DURATION)
    } else if (event.event_type === 'offense') {
      const card = mapFoulCard(event, g, hjc, vjc)
      setActiveFoulCard(card)
      foulDismissTimer.current = setTimeout(() => {
        setActiveFoulCard(null)
        foulDismissTimer.current = null
      }, GOAL_CARD_DURATION)
    } else {
      const card = mapPeriodCard(event, g, hjc, vjc)
      setActivePeriodCard(card)
      periodDismissTimer.current = setTimeout(() => {
        setActivePeriodCard(null)
        periodDismissTimer.current = null
      }, PERIOD_CARD_DURATION)
    }
  }

  // Two separate effects handle VOD vs Live because they have fundamentally
  // different timing sources:
  //
  // VOD: We can map video.currentTime to an absolute timestamp via streamStartedAt,
  // then detect when playback "crosses" an event's occurred_at. This handles seek,
  // rewind, and fast-forward correctly.
  //
  // Live: video.currentTime is unreliable for absolute timestamps (latency, buffering).
  // Instead we detect when scoreboardEvents changes (via WS-triggered refetch) and the
  // last animatable event is new. This avoids missing events that arrive "already in the
  // past" relative to Date.now() due to network delay.

  // --- VOD: crossing-based detection via timeupdate ---
  useEffect(() => {
    const video = videoRef?.current
    if (isLive || !video || !scoreboardEvents) {
      return
    }

    const handleTimeUpdate = () => {
      const {
        scoreboardEvents: events,
        isLive: live,
        streamStartedAt: ssa,
      } = latestRef.current

      if (live || !events) {
        return
      }

      const currentEventTimeMs = getEventTimeMsFromVideo(video, ssa, false)
      if (currentEventTimeMs === 0) {
        return
      }

      const prevTime = prevTimeMs.current
      prevTimeMs.current = currentEventTimeMs
      const now = Date.now()

      // Skip first tick
      if (prevTime === -1) {
        return
      }

      // Seek detected (backward or forward jump > 2s) — cooldown 1s
      // Reset prevTimeMs so post-cooldown tick computes a small forward delta
      const timeDelta = currentEventTimeMs - prevTime
      if (timeDelta < 0 || timeDelta > 2000) {
        cooldownUntil.current = now + 1000
        prevTimeMs.current = currentEventTimeMs
        return
      }

      // Still in cooldown
      if (now < cooldownUntil.current) {
        return
      }

      // Find animatable events crossed between prevTime and currentEventTimeMs
      let crossedEvent: ScoreboardEvent | null = null
      for (let i = 0; i < events.length; i++) {
        const e = events[i]
        if (!e.occurred_at || !e.event_type) {
          continue
        }
        const eventTimeMs = new Date(e.occurred_at).getTime()
        if (eventTimeMs > currentEventTimeMs) {
          break
        }
        if (eventTimeMs > prevTime && ANIMATABLE_TYPES.includes(e.event_type)) {
          crossedEvent = e
        }
      }

      if (crossedEvent?.event_type) {
        showAnimation(crossedEvent)
      }
    }

    video.addEventListener('timeupdate', handleTimeUpdate)
    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoRef, isLive, scoreboardEvents, streamStartedAt])

  // --- Live: detect new events from scoreboard refetch ---
  useEffect(() => {
    if (!isLive || !scoreboardEvents) {
      return
    }

    // Record whether the first scoreboard load already had animatable events
    if (hadEventsOnMount.current === null) {
      const hasAnimatable = scoreboardEvents.some(
        (e) => e.event_type && ANIMATABLE_TYPES.includes(e.event_type)
      )
      hadEventsOnMount.current = hasAnimatable
    }

    if (scoreboardEvents.length === 0) {
      return
    }

    // Find the last animatable event in the scoreboard
    let lastAnimatable: ScoreboardEvent | null = null
    for (let i = scoreboardEvents.length - 1; i >= 0; i--) {
      const e = scoreboardEvents[i]
      if (e.event_type && ANIMATABLE_TYPES.includes(e.event_type)) {
        lastAnimatable = e
        break
      }
    }

    if (!lastAnimatable || !lastAnimatable.event_type) {
      return
    }

    const eventKey = `${lastAnimatable.event_type}-${lastAnimatable.occurred_at}`

    // On first load, record the key. Only skip animating if the initial
    // scoreboard already had events (mid-game join). If the list was empty
    // on mount, this is a genuinely new event — animate it.
    if (lastKnownEventKey.current === '') {
      lastKnownEventKey.current = eventKey
      if (hadEventsOnMount.current !== true) {
        showAnimation(lastAnimatable)
      }
      return
    }

    // New event detected from refetch
    if (eventKey !== lastKnownEventKey.current) {
      lastKnownEventKey.current = eventKey
      showAnimation(lastAnimatable)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scoreboardEvents, isLive])

  // Reset live event tracking when switching away from live mode
  useEffect(() => {
    if (!isLive) {
      lastKnownEventKey.current = ''
      hadEventsOnMount.current = null
    }
  }, [isLive])

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (goalDismissTimer.current) {
        clearTimeout(goalDismissTimer.current)
      }
      if (periodDismissTimer.current) {
        clearTimeout(periodDismissTimer.current)
      }
      if (subDismissTimer.current) {
        clearTimeout(subDismissTimer.current)
      }
      if (foulDismissTimer.current) {
        clearTimeout(foulDismissTimer.current)
      }
    }
  }, [])

  return {
    activeGoalCard,
    activePeriodCard,
    activeSubstitutionCard,
    activeFoulCard,
  }
}
